import { useEffect, useState, createContext, useContext } from 'react';
import styled from '@emotion/styled';
import axios from '../../axios';

import { useNavigate } from 'react-router-dom';

import { Link } from 'react-router-dom';


// table hook
import useTableLoad from '../../hooks/useTableLoad';

// table context
import { TableIdProvider } from '../../contexts/TableId';

// useMutation hook
import { useMutation, useQueryClient } from '@tanstack/react-query';

// dialog for deleting
import Dialog from './Dialog';

// snackbar
import useSnackbarStore from '../../store/snackbar';

import { muiTheme } from '../theme';

import { Skeleton, IconButton, Select, MenuItem, Typography, Tooltip, CircularProgress, Icon } from '@mui/material';

import { formatDisplay, displayTruncated } from '../util';

import { BasicFlex, IconBox } from '../Common';

import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import NorthRoundedIcon from '@mui/icons-material/NorthRounded';
import SouthRoundedIcon from '@mui/icons-material/SouthRounded';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Search from '@mui/icons-material/SearchRounded';


import useIsMobile from '../../hooks/useIsMobile';

import handleErrors from '../errorHandler';

const Table = styled.div`
  /* border: 1px solid ${props => props.theme.colors.darkBg}; */
`;

const TableTitle = styled.div`
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  border-radius: 5px;
  color: ${props => props.theme.colors.primary};
  background-color: ${props => props.theme.colors.primaryFaint};
  width: fit-content;
  padding: .5rem 2rem;
`

const MainTable = styled.div`
  overflow-x: hidden;
`

const ScrollableTable = styled.div`
  overflow-x: auto;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  flex-basis: 1;
  min-width: max-content;
`;

const Cell = styled.div`
  width: 100%;
  /* flex-grow: 1; */
  width: 12rem;
  display: flex;
  justify-content: ${props => props.$align === "right" ? "flex-end" : props.$align === "center" ? "center" : "flex-start"};
  align-items: center;
  padding: .5rem;
  gap: ${props => props.$gap ? props.$gap : "0"};

  @media ${props => props.theme.breaks.down("lg")} {
    width: 12rem;
    padding: .5rem .5rem;
  }
`;

const HeaderRow = styled(Row)`
  background-color: ${props => props.theme.colors.darkBg};
  padding: 1rem;
  font-weight: 700;
  border-radius: 2px;

  @media ${props => props.theme.breaks.down("md")} { 
    font-size: 1.2rem;
  }
`;

const HeaderCell = styled(Cell)`
  gap: .4rem;
  cursor: pointer;

  svg {
    position: relative;
    top: .2rem;
  }

  &:hover .sort-arrow {
    visibility: visible;
  }
`;

const HeaderSelectCell = styled(HeaderCell)`
  min-width: 4.5rem;
  max-width: 4.5rem;
  padding: 0;
  margin-right: .5rem;
  /* border-right: 2px solid ${props => props.theme.colors.border}; */

  /* @media ${props => props.theme.breaks.down('md')} {
    min-width: 6.5rem;
    max-width: 6.5rem;
  } */

`;

const Body = styled.div`
  border-right: 1px solid ${props => props.theme.colors.border};
  border-left: 1px solid ${props => props.theme.colors.border};

  @media ${props => props.theme.breaks.down("md")} {  
    font-size: 1.2rem;
  }
`;

const BodyRow = styled(Row)`
  padding: ${props => props.$margin === "dense" ? ".3rem" : "1rem"} 1rem;
  /* box-shadow: ${props => props.theme.shadows[1]}; */
  /* border-radius: 5px; */
  margin: .8rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  margin: 0;
  background-color: #FFF;
  
`;

const BodyCell = styled(Cell)`
  cursor: ${props => props.$view ? "pointer" : "default"};

  @media ${props => props.theme.breaks.down('lg')} {
    padding: 0 .5rem;
 }
`;

const BodySelectCell = styled(BodyCell)`
  min-width: 4.5rem;
  max-width: 4.5rem;
  border-right: 2px solid ${props => props.theme.colors.border};
  padding: 0;
  margin-right: .5rem;

  /* @media ${props => props.theme.breaks.down('md')} {
    min-width: 6.5rem;
    max-width: 6.5rem;
  } */
`;


const ExpandedComponent = styled.div`
  display: ${props => props.$open ? "block" : "none"};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: .3rem 1rem;
  padding-left: 5.5rem;
  background-color: ${props => props.theme.colors.softBg};
`;


const SkeletonComponent = ({headers, numOfRows = 10}) => {
  return (
    Array.from({ length: numOfRows }, (_, index) => (
      <BodyRow key={index}>
        <BodySelectCell>
          <Skeleton variant="rounded" width={30} height={30} />
        </BodySelectCell>
        {headers.map((header, index) => (
          <BodyCell key={header.id}>
            <Skeleton variant="text" width="100%" />
          </BodyCell>
        ))}
        <BodyCell>
        <Skeleton variant="text" width="100%" />
        </BodyCell>
      </BodyRow>
    ))
  )
}

const viewResource = (navigate, id, view) => {
  if (view) {
    navigate(`view/${id}`);
  }
}


const SimpleTableComponent = ({defaultSort = "-name", headers, rows, mobile_headers, isLoading, idKey="id", Subcomponent, allowActions = true, view = true, url, ...props}) => {

  const { isMobile } = useIsMobile();
  const navigate = useNavigate();

  const [viewCardStatus, setViewCardStatus] = useState({});

  const [margin, setMargin] = useState("normal") // normal, dense

  const toggleOpenViewCard = (id) => {
    setViewCardStatus({...viewCardStatus, [id]: !viewCardStatus[id]});
  }

  const toggleDensity = () => {
    setMargin(prevState => prevState === "normal" ? "dense" : "normal");
  }

  return (
    <Table>

      <MainTable>
        <ScrollableTable>
          <HeaderRow>
            <HeaderSelectCell $align="center">

            </HeaderSelectCell>
            {headers.map((header, index) => (
              <HeaderCell key={header.label} $align={header.align}>
                {isMobile ? displayTruncated(header.label) : header.label}
              </HeaderCell>
            ))}
            {allowActions ? <HeaderCell $align="right">Actions</HeaderCell> : null}
          </HeaderRow>
          <Body>
            {isLoading ? <SkeletonComponent headers={headers} /> : rows.map((row, index) => {
              let isExpanded = viewCardStatus[row[idKey]];
              return (
              <>
              <BodyRow key={row[idKey]} $margin={margin}>
                <BodySelectCell>
                  <IconButton size='small' onClick={() => toggleOpenViewCard(row[idKey])}>
                    {viewCardStatus[row[idKey]] ? <KeyboardArrowDownRoundedIcon color="secondary" /> :
                    <KeyboardArrowRightRoundedIcon color="secondary" /> }
                    </IconButton>
                </BodySelectCell>
                {headers.map(cur => (
                  <BodyCell $view={false} key={cur.id} $align={cur.align} onClick={() => viewResource(navigate, row[idKey], false)}>
                    {formatDisplay(cur, row, isMobile)}
                  </BodyCell>
                ))}
                {allowActions ? <BodyCell $align="right">
                  <BasicFlex justify="flex-end">
                    {view && url ? <Link to={`/${url}/view/${row[idKey]}`}><IconButton size='small'><Search color="secondary" /></IconButton></Link> : null}
                  </BasicFlex>
                </BodyCell> : null}
              </BodyRow>
              <ExpandedComponent $open={isExpanded}>
                {Subcomponent ? <Subcomponent row={row} /> : null}
              </ExpandedComponent>
              </>
            )})}
          </Body>

        </ScrollableTable>
      </MainTable>

    </Table>
  );
};

export default SimpleTableComponent;